import React, { useState } from "react";
import {
  Box, Paper, TextField, MenuItem, ListSubheader, Button, Stack, Grid, Typography, Chip,
} from "@mui/material";
import { Download } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { reportService } from "../../api/services/projectService";
import { PageHeader, Loader, EmptyState } from "../../components/Common";
import DataTable from "../../components/DataTable";
import { useTheme } from "../../contexts/ThemeContext";

// Same fixed hue order used on the Dashboard -- kept local since it's a
// small per-page constant, not worth a shared module for two files.
const PRIORITY_TONE = { Critical: "red", Urgent: "red", High: "amber", Medium: "cyan", Low: "green" };
const FALLBACK_TONES = ["cyan", "amber", "green", "purple", "red"];
// Severity shares Priority's Low/Medium/High/Critical vocabulary, so the
// same semantic colors apply; Team names are arbitrary, so those fall back
// to the plain cycling order.
const DIMENSION_TONE_MAP = { priority: PRIORITY_TONE, severity: PRIORITY_TONE };
const toneFor = (dimension, groupName, idx) =>
  (DIMENSION_TONE_MAP[dimension] || {})[groupName] || FALLBACK_TONES[idx % FALLBACK_TONES.length];

const REPORT_GROUPS = [
  {
    label: "Performance",
    reports: [
      { value: "engineer-performance", label: "Engineer Performance", description: "Tickets handled and SLA hit-rate per engineer." },
    ],
  },
  {
    label: "SLA",
    reports: [
      { value: "sla-violations", label: "SLA Violations", description: "Tickets that missed their response or resolution SLA." },
      { value: "aging", label: "Aging Report", description: "Full details of every currently open ticket, with how long it's been sitting and its age bucket. Always a live snapshot -- date filters don't apply." },
    ],
  },
  {
    label: "Breakdown",
    reports: [
      { value: "priority", label: "Tickets by Priority", description: "Full ticket details -- number, subject, status, team, assignee -- sorted by priority (most urgent first)." },
      { value: "team", label: "Tickets by Team", description: "Full ticket details sorted by team." },
      { value: "phase", label: "Phase-wise Report", description: "Full ticket details per plan (phase/sprint), including unplanned Backlog work." },
    ],
  },
  {
    label: "Trends",
    reports: [
      { value: "monthly-trend", label: "Monthly Trend", description: "Total tickets created per month for a chosen year." },
      { value: "trend", label: "Trend by Priority / Severity / Team", description: "Ticket volume over time, split by the dimension you pick, at daily, weekly, or monthly granularity." },
    ],
  },
];
const ALL_REPORTS = REPORT_GROUPS.flatMap((g) => g.reports);

function pivotTrend(rows) {
  const periods = [];
  const groups = [];
  const seenP = new Set();
  const seenG = new Set();
  for (const r of rows) {
    if (!seenP.has(r.period)) { seenP.add(r.period); periods.push(r.period); }
    if (!seenG.has(r.group)) { seenG.add(r.group); groups.push(r.group); }
  }
  const byPeriod = Object.fromEntries(periods.map((p) => [p, { period: p }]));
  for (const r of rows) byPeriod[r.period][r.group] = r.count;
  return { data: periods.map((p) => byPeriod[p]), groups };
}

function ChartTooltip({ active, payload, label, T }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 2, p: 1.25, boxShadow: T.shadow_soft }}>
      <Typography variant="caption" sx={{ color: T.muted, fontWeight: 700, display: "block", mb: 0.5 }}>{label}</Typography>
      {payload.map((p) => (
        <Stack key={p.dataKey} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
          <Typography variant="caption" sx={{ color: T.text }}>{p.name}: <b>{p.value}</b></Typography>
        </Stack>
      ))}
    </Box>
  );
}

export default function ReportsPage() {
  const { T } = useTheme();
  const [reportName, setReportName] = useState("engineer-performance");
  const [year, setYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dimension, setDimension] = useState("priority");
  const [granularity, setGranularity] = useState("monthly");

  const isAging = reportName === "aging";
  const isMonthlyTrend = reportName === "monthly-trend";
  const isDimensionTrend = reportName === "trend";
  const showDateRange = !isAging && !isMonthlyTrend;
  const currentReport = ALL_REPORTS.find((r) => r.value === reportName);

  const buildParams = () => {
    if (isMonthlyTrend) return { year };
    if (isAging) return {};
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (isDimensionTrend) {
      params.dimension = dimension;
      params.granularity = granularity;
    }
    return params;
  };
  const params = buildParams();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["report", reportName, params],
    queryFn: () => reportService.get(reportName, params),
  });

  const handleExport = async (format) => {
    const response = await reportService.export(reportName, format, buildParams());
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${reportName}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // "aging" returns a dict of buckets rather than a row list -- normalize both shapes for the table
  const rows = Array.isArray(data)
    ? data
    : data
    ? Object.entries(data).map(([bucket, count]) => ({ bucket, count }))
    : [];

  const columns = rows.length > 0
    ? Object.keys(rows[0]).map((key) => ({ key, label: key.replace(/_/g, " ") }))
    : [];

  const { data: trendData, groups: trendGroups } = isDimensionTrend
    ? pivotTrend(Array.isArray(data) ? data : [])
    : { data: [], groups: [] };

  return (
    <Box>
      <PageHeader title="Reports" subtitle="Live report queries with CSV / Excel export" />

      <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={isDimensionTrend ? 4 : 3}>
            <TextField select label="Report" fullWidth value={reportName} onChange={(e) => setReportName(e.target.value)}>
              {REPORT_GROUPS.flatMap((group) => [
                <ListSubheader key={`h-${group.label}`}>{group.label}</ListSubheader>,
                ...group.reports.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>),
              ])}
            </TextField>
          </Grid>

          {isMonthlyTrend && (
            <Grid item xs={12} sm={2}>
              <TextField label="Year" type="number" fullWidth value={year} onChange={(e) => setYear(Number(e.target.value))} />
            </Grid>
          )}

          {isDimensionTrend && (
            <>
              <Grid item xs={6} sm={2.5}>
                <TextField select label="Group by" fullWidth value={dimension} onChange={(e) => setDimension(e.target.value)}>
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="severity">Severity</MenuItem>
                  <MenuItem value="team">Team</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={2.5}>
                <TextField select label="Granularity" fullWidth value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </TextField>
              </Grid>
            </>
          )}

          {showDateRange && (
            <>
              <Grid item xs={6} sm={2}>
                <TextField label="Start date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                  value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="End date" type="date" fullWidth InputLabelProps={{ shrink: true }}
                  value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm="auto" sx={{ ml: "auto" }}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => handleExport("csv")}>CSV</Button>
              <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => handleExport("xlsx")}>Excel</Button>
            </Stack>
          </Grid>
        </Grid>

        {currentReport?.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
            {currentReport.description}
          </Typography>
        )}
      </Paper>

      {isLoading || isFetching ? (
        <Loader label="Running report..." />
      ) : isDimensionTrend ? (
        trendData.length === 0 ? (
          <EmptyState title="No data for this report yet" />
        ) : (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Tickets by {dimension[0].toUpperCase() + dimension.slice(1)}
              </Typography>
              <Chip label={granularity[0].toUpperCase() + granularity.slice(1)} size="small"
                sx={{ background: T.card2, color: T.muted, fontWeight: 600 }} />
            </Stack>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={trendData} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.border} vertical={false} />
                <XAxis dataKey="period" tick={{ fontSize: 12, fill: T.muted }} axisLine={{ stroke: T.border }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: T.muted }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip T={T} />} cursor={{ fill: T.surface_faint }} />
                <Legend wrapperStyle={{ fontSize: 12, color: T.muted }} />
                {trendGroups.map((g, idx) => (
                  <Bar key={g} dataKey={g} name={g} stackId="trend"
                    fill={T[toneFor(dimension, g, idx)]} stroke={T.card} strokeWidth={2} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )
      ) : rows.length === 0 ? (
        <EmptyState title="No data for this report yet" />
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {rows.length} record{rows.length === 1 ? "" : "s"}
          </Typography>
          <DataTable columns={columns} rows={rows} />
        </>
      )}
    </Box>
  );
}
